// Wrapper de tracking de pagos para la SPA. La SPA usa `window.mixpanel` crudo
// (no hay capa de analytics como en la Next), así que este wrapper centraliza
// el guard y el auto-enriquecimiento mínimo para homologar con la Next:
// `country`, `currency` (derivada del país) e `is_logged_in`.
//
// Los eventos se declaran en el catálogo de la Next (fuente de verdad del
// Lexicon) y se replican en `paymentEvents.ts`. Plan:
// propi-web-nextjs/apuntes/plan/payment-tracking.md
import store from '../redux/store'
import { isLoggedIn } from '../helpers/authHelper'
import { PaymentEventName } from './paymentEvents'

// Contexto rico de la pasarela. Lo arma el CHECKOUT (que sí tiene amount/ids) y
// lo pasa como prop opcional `tracking` al motor de pago, que lo spreadea en
// cada evento del funnel. Así Submitted/Completed/Failed llevan flow + amount.
export interface PaymentTrackingContext {
  flow: 'fast-payment' | 'presales' | 'rent' | 'sale'
  amount?: number
  transaction_id?: string
  transaction_detail_id?: string
  contract_id?: string
  project_id?: string
  unit_id?: string
}

// El checkout entrega el total como string formateado ("$1,234.56" / "Q1,234.56").
// Lo dejamos en número para Mixpanel. Quita todo menos dígitos y punto decimal.
export const parseAmount = (formatted?: string | number | null): number | undefined => {
  if (formatted == null) return undefined
  if (typeof formatted === 'number') return Number.isFinite(formatted) ? formatted : undefined
  const n = Number(String(formatted).replace(/[^0-9.]/g, ''))
  return Number.isFinite(n) ? n : undefined
}

// SV y PA operan en USD; GT en GTQ. El backend no devuelve la moneda en el
// checkout, así que se deriva del país.
const currencyForCountry = (country: string): 'USD' | 'GTQ' =>
  String(country).toLowerCase() === 'gt' ? 'GTQ' : 'USD'

// Auto-enriquecimiento mínimo homologado con la Next (country/currency/is_logged_in).
const enrich = (props: Record<string, unknown>) => {
  const state = store.getState()
  const country = state?.country?.country ?? ''
  return {
    ...props,
    country,
    currency: currencyForCountry(country),
    is_logged_in: isLoggedIn(state?.auth),
  }
}

/**
 * Emite un evento de pago a Mixpanel con guard + auto-enriquecimiento.
 * Fire-and-forget: usar para eventos que NO preceden una navegación dura.
 * Para el último evento antes de un redirect/unload, usar `trackPaymentAndWait`.
 */
export const trackPayment = (event: PaymentEventName, props: Record<string, unknown> = {}): void => {
  // El SDK puede no haber cargado (sin token, bloqueado por adblock). No romper
  // el flujo de pago bajo ninguna circunstancia.
  const mp = (window as any)?.mixpanel
  if (!mp || typeof mp.track !== 'function') return
  mp.track(event, enrich(props))
}

const SAFETY_TIMEOUT_MS = 800

/**
 * Variante que devuelve una Promise. Resuelve cuando Mixpanel confirma el envío
 * (callback del SDK) o cuando se cumple el timeout de seguridad de 800ms — lo
 * que pase primero. Homóloga a `trackAndWait` de la Next.
 *
 * Usar con `await` ANTES de una navegación DURA (`window.location.href`,
 * redirect externo del 3DS, `window.open(_, '_self')`), donde un `track()` plano
 * se pierde porque el browser cancela el XHR al unload. NO hace falta para
 * `navigate(...)` de react-router (client-side, sin unload).
 *
 * No tira nunca: si el SDK no cargó o `track` lanza, resuelve igual para no
 * bloquear el flujo de pago.
 */
export const trackPaymentAndWait = (
  event: PaymentEventName,
  props: Record<string, unknown> = {}
): Promise<void> => {
  const mp = (window as any)?.mixpanel
  if (!mp || typeof mp.track !== 'function') return Promise.resolve()

  return new Promise<void>((resolve) => {
    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      resolve()
    }
    try {
      mp.track(event, enrich(props), done)
    } catch (e) {
      console.error('[trackPayment] trackPaymentAndWait falló:', event, e)
      done()
      return
    }
    setTimeout(done, SAFETY_TIMEOUT_MS)
  })
}
